<template>
    <div class="box-header">
        <span>
            <span class="goal-heading">
                <span v-if="editState == 1">
                    <context-button :menu-contents-function="contextMenu"
                                    :index="recoveryGoalBlock.goalPlanRecoveryGoalBlockId" />
                </span>
                Goal {{ recoveryGoalBlockNumber }}
            </span>
            <span class="pull-right-indented">
                <span v-if="recoveryGoalBlock.reviewStatus"
                      class="pill"
                      :style="backgroundColor">{{ recoveryGoalBlock.reviewStatus }}</span>
                <span v-if="showModified"
                      class="modified-pill">
                    Modified
                </span>
                <span v-if="showCollapser">
                    <button type="button"
                            class="collapser-btn"
                            @click="collapserToggled">
                        <i v-if="closed"
                           class="icon icon-closed-collapser" />
                        <i v-else
                           class="icon icon-open-collapser" />
                    </button>
                </span>
                <!-- wrap the button inside a span
                 so that she is the only child  -->
                <span v-if="editable">
                    <button type="button"
                            class="btn portal-btn outline icon-button"
                            @click="editClicked">
                        <i class="icon iconbutton icon-pencil" />
                    </button>
                </span>
            </span>
        </span>
    </div>
    <div class="box-body">
        <template v-if="!closed">
            <recovery-goal-items ref="recoveryGoalItems"
                                 v-model="recoveryGoalBlock"
                                 :history="history"
                                 :patient-id="patientId"
                                 :recovery-goal-block-id="recoveryGoalBlock.goalPlanRecoveryGoalBlockId"
                                 :edit-state="editState"
                                 :edit-mode-on="editModeOn"
                                 :url-head="urlHead"
                                 @cancel-pressed="cancelPressed"
                                 @delete-pressed="deletePressed"
                                 @update-pressed="updatePressed"
                                 @update:model-value="goalItemEdited" />
            <recovery-goal-review v-if="canBeReviewed"
                                  v-model="recoveryGoalBlock"
                                  :patient-id="patientId"
                                  :edit-state="editState"
                                  :review-statuses="reviewStatuses"
                                  :url-head="urlHead" />
        </template>
    </div>
    <load-preset ref="loadPresetDlg"
                 v-model="selectedPreset"
                 :id-number="recoveryGoalBlock.goalPlanRecoveryGoalBlockId"
                 type="0"
                 @update:model-value="presetSelectedForLoad" />
    <save-preset ref="savePresetDlg"
                 :data="recoveryGoalBlock"
                 :id-number="recoveryGoalBlock.goalPlanRecoveryGoalBlockId"
                 :goal-plan-id="goalPlanId"
                 type="0" />
    <delete-preset ref="deletePresetDlg"
                   :id-number="recoveryGoalBlock.goalPlanRecoveryGoalBlockId"
                   type="0" />
</template>

<script setup>
    import { defineProps, defineEmits, ref, computed, onMounted, nextTick } from 'vue';
    import axios from 'axios';
    import { axiosError, axiosConfig } from '../../js/AjaxUtilities.js';
    import RecoveryGoalItems from './RecoveryGoalItems.vue';
    import RecoveryGoalReview from './RecoveryGoalReview.vue';
    import ContextButton from '../ContextButton.vue';
    import LoadPreset from './LoadPreset.vue';
    import SavePreset from './SavePreset.vue';
    import DeletePreset from './DeletePreset.vue';
    import { canEdit } from '../../js/CalcGoalPlanEditState.js';
    import { toHexColorString } from '../../js/NumberUtilities.js';


    const props = defineProps({
        modelValue: {
            type: Object,
            default: null
        },
        recoveryGoalBlockNumber: {
            type: Number,
            default: 0
        },
        patientId: {
            type: Number,
            default: 0
        },
        editState: {
            type: Number,
            default: 0
        },
        reviewStatuses: {
            type: Array,
            default: null
        },
        canBeReviewed: {
            type: Boolean,
            default: false
        },
        urlHead: {
            type: String,
            default: null
        },
        editModeOnCreate: {
            type: Boolean,
            default: false
        },
        goalPlanId: {
            type: Number,
            default: 0
        }
    });

    onMounted(() => {
        editModeOn.value = props.editModeOnCreate;
        if (showCollapser.value) {
            closed.value = true; // if we are a closed goal, start with it closed
        }
    });

    const emit = defineEmits(['update:modelValue', 'deletePressed', 'cancelPressed', 'updatePressed']);

    const editModeOn = ref(false);
    const closed = ref(false);

    const recoveryGoalBlock = computed({
        get: () => props.modelValue,
        set: newValue => emit("update:modelValue", newValue)
    });

    const editable = computed(() => canEdit(props.editState, 2) && !recoveryGoalBlock.value.isClosed);
    const backgroundColor = computed(() => `background-color: ${toHexColorString(recoveryGoalBlock.value.colour)}`)
    const showModified = computed(() => recoveryGoalBlock.value.itemsModified && props.editState == 1);
    const showCollapser = computed(() => recoveryGoalBlock.value.isClosed);

    const selectedPreset = ref(0);
    const loadPresetDlg = ref(null);
    const savePresetDlg = ref(null);
    const deletePresetDlg = ref(null);

    const recoveryGoalItems = ref(null);
    const history = ref(null);

    function editClicked() {
        editModeOn.value = true;
        loadHistory();
    }

    function cancelPressed() {
        goalItemEdited();
        nextTick(() => emit('cancelPressed')); 
    }

    function goalItemEdited() {
        editModeOn.value = false;
    }

    function deletePressed(recoveryGoalBlockId) {
        emit("deletePressed", recoveryGoalBlockId);
    }

    function updatePressed(updateGoalItems) {
        goalItemEdited();
        nextTick(() => emit('updatePressed', updateGoalItems));
    }

    function contextMenu(index) {
        return [
            {
                customClass: "icon icon-import",
                label: "Load preset",
                onClick: () => {
                    loadGoalPreset(index);
                }
            },
            {
                customClass: "icon icon-export",
                label: "Save preset",
                onClick: () => {
                    saveGoalPreset(index);
                }
            },
            {
                customClass: "icon icon-delete",
                label: "Delete preset",
                onClick: () => {
                    deleteGoalPreset(index);
                }
            },
        ];
    }

    function loadGoalPreset(index) {
        loadPresetDlg.value.show();
    }

    function saveGoalPreset(index) {
        savePresetDlg.value.show();
    }

    function deleteGoalPreset(index) {
        deletePresetDlg.value.show();
    }

    function presetSelectedForLoad() {
        let presetId = selectedPreset.value;

        axios
            .post(`/GoalPlan/LoadPreset`, presetId, axiosConfig())
            .then(function (response) {
                if (response.data.success) {
                    editWithPreset(response.data.preset);
                } else {
                    alert(`Failed to load preset: ${response.data.message}`);
                }
            })
            .catch(function (error) {
                axiosError("Load presets", axios, error);
            })
    }

    function editWithPreset(preset) {
        recoveryGoalItems.value.setEditData(preset.recoveryGoalItems);
        editModeOn.value = true;
    }

    function loadHistory() {
        if (!showModified.value) {
            history.value = null;
        } else if (!history.value) {
            let data = {
                patientId: props.patientId,
                id: recoveryGoalBlock.value.goalPlanRecoveryGoalBlockId
            };

            axios
                .post(`/GoalPlan/GetPreviousRecoveryGoalItems`, data, axiosConfig())
                .then(function (response) {
                    if (response.data.success) {
                        history.value = response.data.recoveryGoalItems;
                    } else {
                        alert(`Failed to load goal history: ${response.data.message}`);
                    }
                })
                .catch(function (error) {
                    axiosError("Get previous recovery goals", axios, error);
                })
        }
    }

    function collapserToggled() {
        closed.value = !closed.value;
    }


</script>

<style scoped>
    .goal-heading {
        font-size: 110%;
        font-weight: bold;
    }

</style>